import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "../../components/SortableItem";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";


const ViewSalaryOrder = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [rowOrder, setRowOrder] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const toastShownRef = useRef(false);

  const mainPage = "/Payroll/SetSalary/list";
  const managePage = "Set Salary Order";
  const token = localStorage.getItem("authToken");

  // Fetch salary order data from API
  const fetchSalaryOrder = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}payroll/view-salary-order`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.status === 1) {
        setItems(response.data.data || []);
        if (!toastShownRef.current) {
          showToast.success(response.data.message);
          toastShownRef.current = true;
        }
      } else {
        setError(response.data.message || "Failed to fetch salary order");
        showToast.error(
          response.data.message || "Failed to fetch salary order"
        );
      }
    } catch (error) {
      console.error("Error fetching salary order:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An error occurred while fetching data";
      setError(errorMessage);
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSalaryOrder();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Updated saveOrder function with axios POST request
  const saveOrder = async () => {
    if (items.length === 0) {
      showToast.error("No items to save");
      return;
    }

    try {
      setSaving(true);
      const selectedOrder = items.map((item) => item.id);
      setRowOrder(selectedOrder.join(","));

      // Prepare the data payload
      const payload = {
        row_order: selectedOrder.join(","),
        // You can add more fields if needed based on your API requirements
        items: selectedOrder,
      };

      const response = await axios.post(
        `${API_URL}payroll/save-salary-order`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            accept: "*/*",
          },
        }
      );

      if (response.data.status === 1 || response.status === 200) {
        showToast.success(response.data.message || "Order saved successfully!");
        // Optionally refresh the data after successful save
        // await fetchSalaryOrder();
      } else {
        showToast.error(response.data.message || "Failed to save order");
      }
    } catch (error) {
      console.error("Error saving order:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error saving order. Please try again.";
      showToast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  // Loading state
  if (loading) {
    return <LoadingSpinner />;
  }

  // Error state
  if (error) {
    return (
      <div
        id="page-container"
        className="page-header-fixed page-sidebar-fixed fade in"
      >
        <div id="content" className="">
          <div className="row pb-2">
            <div className="col-md-6">
              <h4 className="panel-title pull-left">{managePage}</h4>
            </div>
            <div className="col-md-6" style={{ textAlign: "right" }}>
              <button
                onClick={() => navigate(mainPage)}
                className="btn btn-default btn-sm"
              >
                <b>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </b>{" "}
                Return to Set Salary
              </button>
            </div>
          </div>

          <div className="panel panel-default with-rounded-corner with-shadow">
            <div className="text-center p-4">
              <div className="alert alert-danger">
                <strong>Error:</strong> {error}
              </div>
              <button
                onClick={fetchSalaryOrder}
                className="btn btn-primary btn-sm"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      {/* Header would be included here from your layout */}

      <div id="content" className="">
        {/* Alert box component would go here */}

        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">{managePage}</h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <button
              onClick={() => navigate(mainPage)}
              className="btn btn-default btn-sm"
              disabled={saving}
            >
              <b>
                <FontAwesomeIcon icon={faChevronLeft} />
              </b>{" "}
              Return to Set Salary
            </button>
            {/* <button 
              onClick={fetchSalaryOrder} 
              className="btn btn-success btn-sm ms-2"
              disabled={loading || saving}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button> */}
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          {items.length > 0 ? (
            <div className="table-responsive">
              <form name="checkForm">
                <input
                  type="hidden"
                  name="row_order"
                  id="row_order"
                  value={rowOrder}
                />

                {/* Instructions */}
                <div className="alert alert-info m-3">
                  <i className="fa fa-info-circle"></i>
                  <strong> Instructions:</strong> Drag and drop the items below
                  to reorder them. Click "Save Order" when you're done.
                </div>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={items}
                    strategy={verticalListSortingStrategy}
                  >
                    <ul
                      id="sortable-row"
                      style={{
                        listStyle: "none",
                        width: "100%",
                        paddingRight: "30px",
                        paddingTop: "30px",
                        paddingLeft: "30px",
                      }}
                    >
                      {items.map((item, index) => (
                        <SortableItem key={item.id} id={item.id}>
                          <li
                            style={{
                              marginBottom: "10px",
                              padding: "5px",
                              backgroundColor: "#f7f7f7",
                              cursor: "move",
                              borderRadius: "8px",
                              paddingLeft: "15px",
                              border: "1px #c1c1c1 solid",
                              transition: "all 0.2s ease",
                              display: "flex",
                              alignItems: "center",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                          >
                            <span
                              style={{
                                backgroundColor: "#007bff",
                                // backgroundColor: "#151515",
                                color: "white",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                                fontWeight: "bold",
                                marginRight: "15px",
                              }}
                            >
                              {index + 1}
                            </span>
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              style={{ color: "#28a745", marginRight: "10px" }}
                            />
                            <span
                              style={{ fontSize: "14px", fontWeight: "500" }}
                            >
                              {item.title}
                            </span>
                            <span
                              style={{
                                marginLeft: "auto",
                                color: "#6c757d",
                                fontSize: "12px",
                              }}
                            >
                              {/* ID: {item.id} */}
                            </span>
                          </li>
                        </SortableItem>
                      ))}
                    </ul>
                  </SortableContext>
                </DndContext>

                <div style={{ textAlign: "center", padding: "20px" }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveOrder}
                    disabled={saving || items.length === 0}
                    // style={{ minWidth: "150px" }}
                  >
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-save me-2"></i>
                        Save Order
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="text-center p-5">
              <i className="fa fa-list-ol fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">No salary records found</h5>
              <p className="text-muted">
                There are no salary items available to order.
              </p>
              <button
                onClick={fetchSalaryOrder}
                className="btn btn-primary btn-sm"
              >
                <i className="fa fa-refresh me-1"></i>
                Refresh
              </button>
            </div>
          )}
        </div>

        {/* Order Summary */}
        {/* {items.length > 0 && (
          <div className="panel panel-info with-rounded-corner with-shadow mt-3">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-info-circle me-2"></i>
                Order Summary
              </h4>
            </div>
            <div className="panel-body">
              <div className="row">
                <div className="col-md-6">
                  <strong>Total Items:</strong> {items.length}
                </div>
                <div className="col-md-6">
                  <strong>Current Order:</strong> {items.map(item => item.id).join(', ')}
                </div>
              </div>
            </div>
          </div>
        )} */}

        {/* Scroll to top button */}
        <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
          <i className="ti-arrow-up"></i>
        </a>
      </div>
    </div>
  );
};

export default ViewSalaryOrder;
